import { DialogComponent } from '@theme/dialog';

/**
 * @typedef {object} PurchaseConfirmationConfig
 * @property {string} groupLabel
 * @property {string} value
 * @property {string} modalTitle
 * @property {string} modalQuestion
 * @property {string} cancelLabel
 * @property {string} confirmLabel
 */

/**
 * Displays the option groups selected for the current product and resolves
 * the purchase attempt only after the customer confirms or cancels.
 */
class UnilarPurchaseConfirmation extends DialogComponent {
  requiredRefs = ['dialog', 'title', 'options', 'question', 'cancelButton', 'confirmButton'];

  /** @type {Promise<boolean> | null} */
  #pendingConfirmation = null;

  /** @type {((confirmed: boolean) => void) | null} */
  #resolveConfirmation = null;

  /** @type {boolean} */
  #result = false;

  /** @type {HTMLElement | null} */
  #trigger = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('close', this.#handleClose);
  }

  disconnectedCallback() {
    this.refs.dialog?.removeEventListener('close', this.#handleClose);
    this.#resolve(false);
    super.disconnectedCallback();
  }

  /**
   * @param {PurchaseConfirmationConfig[]} configs
   * @param {HTMLElement | null} trigger
   * @returns {Promise<boolean>}
   */
  requestConfirmation(configs, trigger = null) {
    if (this.#pendingConfirmation) return this.#pendingConfirmation;

    const validConfigs = configs.filter((config) => String(config.value || '').trim() !== '');
    if (validConfigs.length === 0) return Promise.resolve(true);

    this.#populate(validConfigs);
    this.#result = false;
    this.#trigger = trigger;
    this.#pendingConfirmation = new Promise((resolve) => {
      this.#resolveConfirmation = resolve;
    });

    this.showDialog();
    requestAnimationFrame(() => this.refs.cancelButton.focus());

    return this.#pendingConfirmation;
  }

  handleCancel = () => {
    this.#result = false;
    this.closeDialog();
  };

  handleConfirm = () => {
    this.#result = true;
    this.closeDialog();
  };

  /** @param {PurchaseConfirmationConfig[]} configs */
  #populate(configs) {
    const copy = configs[0];
    this.refs.title.textContent = copy.modalTitle || '';
    this.refs.question.textContent = copy.modalQuestion || '';
    this.refs.cancelButton.textContent = copy.cancelLabel || '';
    this.refs.confirmButton.textContent = copy.confirmLabel || '';
    this.refs.options.replaceChildren();

    for (const config of configs) {
      const item = document.createElement('div');
      item.className = 'unilar-purchase-confirmation__option';

      const label = document.createElement('dt');
      label.className = 'unilar-purchase-confirmation__option-label';
      label.textContent = config.groupLabel || '';

      const value = document.createElement('dd');
      value.className = 'unilar-purchase-confirmation__option-value';
      value.textContent = config.value;

      item.append(label, value);
      this.refs.options.append(item);
    }
  }

  #handleClose = () => {
    this.#resolve(this.#result);
  };

  /** @param {boolean} result */
  #resolve(result) {
    const resolve = this.#resolveConfirmation;
    const trigger = this.#trigger;

    this.#pendingConfirmation = null;
    this.#resolveConfirmation = null;
    this.#trigger = null;
    this.#result = false;

    resolve?.(result);
    if (trigger?.isConnected) trigger.focus();
  }
}

if (!customElements.get('unilar-purchase-confirmation')) {
  customElements.define('unilar-purchase-confirmation', UnilarPurchaseConfirmation);
}
